import React, {
    useState,
    ReactNode,
    useEffect,
    useContext
} from 'react';
import { useRouter } from 'next/router';
import jwt_decode from 'jwt-decode';
import * as Sentry from '@sentry/nextjs';
import { getAccessToken } from '@/shared/http';

// Helpers
import buildLayout from '@helpers/build-layout';

// Components
import NProgress from '@components/utils/nprogress';
import ListItems from '@lib/layout/menu';
import ScreenHeader from '@lib/layout/header';
import Snackbar from '@/src/components/UI-KIT/Snackbar';

// Material UI
import {
    Grid,
    createMuiTheme,
    MuiThemeProvider
} from '@material-ui/core';

// Types
import { User } from '@services/user';
import { Organization } from '@services/session';

// Styles
import './Layout.scss';
import { getOrganizationData } from '@/src/services/session';

export const THEME = createMuiTheme({
    typography: {
        fontFamily: [ 'Poppins', 'sans-serif' ].join(', ')
    }
});

export const OrganizationContext = React.createContext<{ organization: Organization }>({
    organization: {
        id: null,
        name: '',
        website: '',
        email: '',
        logo: '',
        phone: '',
        description: '',
        address: null,
        signature: ''
    }
});

export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within a OrganizationContext provider');
    }

    return context;
};

export const SnackbarContext = React.createContext({
    setSnackbar: (_snackbar: {
        snackbarMessage: string,
        snackbarIcon: ReactNode,
        snackbarColor: 'neutral' | 'success' | 'error'
    }) => {}
});

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarContext provider');
    }

    return context;
};

export const HeaderContext = React.createContext({
    setCreateButton: (_createButton: { label: string, icon: ReactNode | null, action: () => void }) => {},
    setHeaderChildren: (_headerChildren: ReactNode) => {},
    expanded: true,
    setHero: (_hero: ReactNode) => {},
    setHideHeader: (_hideHeader: boolean) => {}
});

export const useHeader = () => {
    const context = useContext(HeaderContext);
    if (!context) {
        throw new Error('useHeader must be used within a HeaderContext provider');
    }

    return context;
};

interface Props {
    children: ReactNode
}

const defaultSnackbar = {
    snackbarMessage: '',
    snackbarIcon: null,
    snackbarColor: 'neutral' as 'neutral' | 'success' | 'error'
};

const MainLayout: React.FC<Props> = ({ children }) => {
    const [ expanded, setExpanded ] = useState(true);
    const [ hero, setHero ] = useState(false);
    const [ organization, setOrganization ] = useState(null);
    const [ createButton, setCreateButton ] = useState<{ label: string, icon: ReactNode | null, action: () => void } | null>(null);
    const [ headerChildren, setHeaderChildren ] = useState<ReactNode>();
    const [ hideHeader, setHideHeader ] = useState(false);
    const [ title, setTitle ] = useState('');
    const [
        {
            snackbarMessage,
            snackbarIcon,
            snackbarColor
        },
        setSnackbar
    ] = useState(defaultSnackbar);
    const [ snackbarOpened, setSnackbarOpened ] = useState(false);

    useEffect(() => {
        // don't render the first time
        if (snackbarMessage === defaultSnackbar.snackbarMessage
            && snackbarIcon === defaultSnackbar.snackbarIcon
            && snackbarColor === defaultSnackbar.snackbarColor)
            return;

        setSnackbarOpened(true);
    }, [
        snackbarMessage,
        snackbarIcon,
        snackbarColor
    ]);

    useEffect(() => {
        if (!snackbarOpened) {
            return;
        }

        setTimeout(() => setSnackbarOpened(false), 3000);
    }, [ snackbarOpened ]);

    const url = useRouter().pathname;

    const getOrganization = async () => {
        const { data } = await getOrganizationData();
        setOrganization(data);
    };

    useEffect(() => {
        switch (true) {
            case /inicio\/\w?/.test(url):
                setTitle('Inicio');
                break;
            case /cuentas\/\w?/.test(url):
                setTitle('Cuentas');
                break;
            case /contratos\/\w?/.test(url):
                setTitle('Contratos');
                break;
            case /propietarios\/\w?/.test(url):
                setTitle('Propietarios');
                break;
            case /propiedades\/\w?/.test(url):
                setTitle('Propiedades');
                break;
            case /inquilinos\/\w?/.test(url):
                setTitle('Inquilinos');
                break;
            case /garantes\/\w?/.test(url):
                setTitle('Garantes');
                break;
            case /alquileres\/\w?/.test(url):
                setTitle('Cobrar alquiler');
                break;
            case /liquidaciones\/\w+/.test(url):
                setTitle('Liquidar propiedad');
                break;
            default: break;
        }
        const accessToken = getAccessToken('access_token');
        const user: User = jwt_decode(accessToken);
        Sentry.setUser({
            id: user.id.toString(),
            email: user.email
        });
        Sentry.setContext('Organization', {
            id: user.organization,
            roles: user.roles
        });
        getOrganization();
    }, [ ]);

    return (
        <MuiThemeProvider
            theme={THEME}
        >
            <OrganizationContext.Provider
                value={{ organization }}
            >
                <Grid
                    container
                    style={{ width: '100vw' }}
                >
                    <ListItems
                        expanded={expanded}
                        setExpanded={setExpanded}
                    />
                    {!hideHeader && (
                        <ScreenHeader
                            expanded={expanded}
                            title={title}
                            createButton={createButton}
                            children={headerChildren}
                        />
                    )}
                    <HeaderContext.Provider
                        value={{
                            setCreateButton,
                            setHeaderChildren,
                            expanded,
                            setHero,
                            setHideHeader
                        }}
                    >
                        {hero && (
                            <Grid
                                container
                                className={`AOL-Hero-container${expanded ? ' menu-expanded' : ' menu-collapsed'}`}
                            >
                                {hero}
                            </Grid>
                        )}
                        <Grid
                            container
                            className={`AOL-Pages-container${expanded ? ' menu-expanded' : ' menu-collapsed'}${!hero ? ' without-hero' : ''}`}
                        >
                            <SnackbarContext.Provider
                                value={{
                                    setSnackbar
                                }}
                            >
                                {children}
                            </SnackbarContext.Provider>
                        </Grid>
                    </HeaderContext.Provider>
                    <NProgress />
                </Grid>
                <Snackbar
                    message={snackbarMessage}
                    icon={snackbarIcon}
                    color={snackbarColor}
                    open={snackbarOpened}
                    setOpen={setSnackbarOpened}
                />
            </OrganizationContext.Provider>
        </MuiThemeProvider>
    );
};

export default buildLayout(MainLayout);
